import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Drawer, Select, Input, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { RedoOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { RCTable, ButtonWrapper } from 'components';
import { ACL, FilterTitle } from 'components/common';
import { Box, Flex, Text } from 'atoms';
import { getListAction } from 'store/pageSlice';
import { searchEmployeeV2, getEmployeeFiltersV2 } from 'store/employeeSliceV2';
import { UserManagementTableConfig, UserListQueries, initialPaginateConfig } from 'utils';
import { useQueryParams } from 'hooks';
import { FilterAtnButton } from 'utils/commonStyled';
import { getUIVersion } from 'utils/router-static-data';

const { Search } = Input;

const LtSearchBar = styled(Search)`
	border: none;
`;

const allOption = {
	value: '',
	label: 'All',
};

const StatusOptions = [
	{ value: 'true', label: 'Active' },
	{ value: 'false', label: 'Inactive' },
];
StatusOptions.unshift(allOption);

const UserTypesOptions = [
	{ value: 'admin', label: 'Admin' },
	{ value: 'supervisor', label: 'Supervisor' },
	{ value: 'business', label: 'Business' },
	{ value: 'city_manager', label: 'City Manager' },
	{ value: 'management', label: 'Management' },
	{ value: 'control_tower', label: 'Control Tower' },
	{ value: 'finance', label: 'Finance' },
];
UserTypesOptions.unshift(allOption);

function populateOptions(data = [], showAll = true) {
	/**
	 * assign id & value to option list
	 */
	data = data?.map((element) => ({
		value: element?.id,
		label: element?.value,
	}));

	/**
	 * Add All option of array first
	 */
	if (showAll) {
		data.unshift(allOption);
	}

	return data;
}

export default function EmployeeManagementPage() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [citiesList, setCitiesList] = useState('');
	const [clientsList, setClientsList] = useState('');
	const [projectsList, setProjectsList] = useState('');
	const [businessUnitsList, setBusinessUnitsList] = useState([]);
	const [rowData, setRowData] = useState({});
	const [open, setOpen] = useState(false);
	const [paginateConfig, setpaginateConfig] = useQueryParams({
		search_type: 'employee',
		filterQuery: UserListQueries,
		persistKey: 'employeeList:filters',
	});

	// Check UI version for conditional API calls
	// This determines which API version to use based on localStorage 'uiVersion' key
	const uiVersion = getUIVersion();
	const useV2API = uiVersion === 'new';

	// business_unit_ids is now always an array thanks to useQueryParams transformation
	const businessUnitIds = paginateConfig?.business_unit_ids || [];

	const StatusValue = paginateConfig?.is_active ? 'Status' : '';
	const UserVal = paginateConfig?.user_type ? 'User Type' : '';
	const cityFilterValue = paginateConfig?.city ? 'City' : '';
	const client = paginateConfig?.client_id || paginateConfig?.client_name ? 'Client' : '';
	const project = paginateConfig?.project_id ? 'Project' : '';
	const businessUnitFilterValue = businessUnitIds.length > 0 ? 'Business Unit' : '';
	const StatusValueAll = paginateConfig?.is_active === '' ? 'Status' : '';
	const UserAllVal = paginateConfig?.user_type === '' ? 'User Type' : '';
	const clientAll =
		paginateConfig?.client_id === '' || paginateConfig?.client_name === '' ? 'Client' : '';
	const projectAll = paginateConfig?.project_id === '' ? 'Project' : '';

	const paginationArry = [
		cityFilterValue,
		StatusValue,
		UserVal,
		client,
		project,
		businessUnitFilterValue,
		projectAll,
		StatusValueAll,
		UserAllVal,
		clientAll,
	];
	const validArray = paginationArry.filter((n) => n);
	console.log(validArray.length);

	// Helper function to map V1 filter params to V2 filter_by structure
	const mapFiltersToV2 = (rest) => ({
		...(rest.query_str && { query_str: rest.query_str }),
		...(rest.is_active !== undefined &&
			rest.is_active !== '' && {
				is_active: rest.is_active === 'true' || rest.is_active === true,
			}),
		...(rest.user_type && { user_type: rest.user_type }),
		...(rest.city && { city: rest.city }),
		// V2 uses client_name (string) - use client_name if available
		...(rest.client_name && { client_name: rest.client_name }),
		...(rest.project_id && { project_id: rest.project_id }),
		...(rest.business_unit_ids &&
			Array.isArray(rest.business_unit_ids) &&
			rest.business_unit_ids.length > 0 && {
				business_unit_ids: rest.business_unit_ids.map(Number),
			}),
	});

	// Conditional dispatch action for RCTable
	// CRITICAL: When useV2API is true, this MUST return V2 action, never getListAction
	const dispatchAction = useMemo(() => {
		if (useV2API) {
			// V2 API wrapper - return thunk function, not dispatch result
			// This ensures RCTable calls V2 API when "New UI" toggle is active
			return (config) => {
				const { offset, limit, search_type, ...rest } = config;
				const filter_by = mapFiltersToV2(rest);

				// Return the thunk function directly, RCTable will dispatch it
				// This will call /api/v2/search
				return searchEmployeeV2({
					filter_by,
					offset: Number(offset) || 0,
					limit: Number(limit) || 20,
				});
			};
		}
		// V1 API (default) - only used when useV2API is false
		// This will call /api/v1/search
		return getListAction;
	}, [useV2API]);

	// Fetch filters - V1 includes in search response, V2 needs separate call
	// V1: Fetch filters from search response (ONLY when V1 API is active)
	// CRITICAL: This effect MUST NOT call V1 API when useV2API is true
	useEffect(() => {
		// CRITICAL GUARD: If V2 API is active, do NOT execute ANY code
		// This prevents the V1 API call to /api/v1/search with search_type: 'employee'
		// This check must be the FIRST thing in the effect
		if (useV2API) {
			return; // Exit immediately - V2 filters are fetched separately when drawer opens
		}

		// Additional safety check: only proceed if this is employee search and V1 is active
		if (paginateConfig?.search_type !== 'employee') {
			return; // Not employee search, skip
		}

		// V1: Fetch filters from search response
		// This will call /api/v1/search with search_type: 'employee'
		// Only executes when useV2API is false AND search_type is 'employee'
		dispatch(getListAction(paginateConfig)).then((res) => {
			let { cities = [], clients = [], projects = [] } = res;

			cities = cities?.map((element) => ({
				value: element,
				label: element,
			}));

			clients = populateOptions(clients);
			projects = populateOptions(projects);

			setCitiesList(cities);
			setClientsList(clients);
			setProjectsList(projects);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, paginateConfig, useV2API]);

	// Fetch cities - Conditionally use V1 or V2 API based on UI version
	useEffect(() => {
		// CRITICAL: Skip V1 API call when V2 is active
		if (useV2API) {
			// Cities will be fetched from V2 when drawer opens
			return;
		}

		// V1: Fetch cities using V1 API
		dispatch(getListAction({ search_type: 'employee', limit: 1, offset: 0 })).then((res) => {
			let { cities = [] } = res;
			cities = cities?.map((element) => ({
				value: element,
				label: element,
			}));
			setCitiesList(cities);
		});
	}, [dispatch, useV2API]);

	// Fetch clients on mount for V2 if client_id exists in URL (to map client_id to client_name)
	useEffect(() => {
		if (useV2API && paginateConfig?.client_id && !paginateConfig?.client_name) {
			dispatch(getEmployeeFiltersV2({ filter_list: ['clients'] })).then((res) => {
				if (res?.clients) {
					const clients = populateOptions(res.clients || []);
					const selectedClient = clients.find(
						(c) =>
							c.value === paginateConfig.client_id ||
							c.value === String(paginateConfig.client_id)
					);
					if (selectedClient?.label) {
						setpaginateConfig((prev) => ({
							...prev,
							client_name: selectedClient.label,
						}));
					}
					// Also set clientsList for the Select component
					setClientsList(clients);
				}
			});
		}
	}, [
		dispatch,
		useV2API,
		paginateConfig?.client_id,
		paginateConfig?.client_name,
		setpaginateConfig,
	]);

	// Helper function to update client name if client_id exists
	const updateClientNameIfNeeded = useCallback(
		(clients) => {
			if (!paginateConfig?.client_id || paginateConfig?.client_name) {
				return;
			}
			const selectedClientItem = clients.find(
				(c) =>
					c.value === paginateConfig.client_id ||
					c.value === String(paginateConfig.client_id)
			);
			if (selectedClientItem?.label) {
				setpaginateConfig((prev) => ({
					...prev,
					client_name: selectedClientItem.label,
				}));
			}
		},
		[paginateConfig?.client_id, paginateConfig?.client_name, setpaginateConfig]
	);

	// Fetch V2 filters when drawer opens (including cities for V2)
	useEffect(() => {
		if (!useV2API || !open) {
			return;
		}
		// Fetch cities, clients, and projects from V2
		dispatch(getEmployeeFiltersV2({ filter_list: ['cities', 'clients', 'projects'] })).then(
			(res) => {
				if (!res) {
					return;
				}
				// Set cities from V2 response
				const cities = res.cities || [];
				const clients = populateOptions(res.clients || []);
				setCitiesList(cities);
				setClientsList(clients);
				setProjectsList(populateOptions(res.projects || []));
				updateClientNameIfNeeded(clients);
			}
		);
	}, [dispatch, open, useV2API, updateClientNameIfNeeded]);

	// Fetch business units
	useEffect(() => {
		dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: 100,
				offset: 0,
			})
		).then((res) => {
			const buOptions = (res?.rows || []).map((bu) => ({
				value: bu.id,
				label: bu.name,
			}));
			// Don't add "All" option for multiple select filters
			setBusinessUnitsList(buOptions);
		});
	}, [dispatch]);
	console.log(paginateConfig);

	localStorage.setItem('employee', JSON.stringify(paginateConfig));
	console.log(localStorage.employee);

	// const exportfunc = () => {
	// 	const payload = {
	// 		report_type: 'employee_csv_export',
	// 		file_type: 'csv',
	// 		data: {
	// 			is_active: paginateConfig?.is_active,
	// 			city: paginateConfig?.city,
	// 			user_type: paginateConfig?.user_type,
	// 			project_id: paginateConfig?.project_id,
	// 			vendor_id: paginateConfig?.vendor_id,
	// 			client_id: paginateConfig?.client_id,
	// 			url_type: 'download',
	// 		},
	// 	};

	// 	dispatch(exportSlice({ data: payload })).then((res) => {
	// 		console.log('res', res);
	// 	});
	// };

	return (
		<>
			<Flex justifyContent="space-between" mt={3}>
				<Box flex="0 0 20%">
					<LtSearchBar
						enterButton="Search"
						placeholder="Search"
						allowClear
						onSearch={(val) =>
							setpaginateConfig({
								...initialPaginateConfig,
								query_str: val,
								search_type: 'employee',
							})
						}
					/>
				</Box>
				<Box flex="0 0 35%" className="filter-hightlight">
					<Box flex="0 0 35%" display="flex">
						<FilterAtnButton
							onClick={() => {
								setOpen(true);
							}}
						>
							FILTERS
						</FilterAtnButton>
						<span className="highlight-list">
							{StatusValue && <span className="ant-tag">{StatusValue} </span>}
							{StatusValueAll && <span className="ant-tag">{StatusValueAll} </span>}
							{projectAll && <span className="ant-tag">{projectAll} </span>}
							{UserVal && <span className="ant-tag">{UserVal} </span>}
							{cityFilterValue && <span className="ant-tag">{cityFilterValue} </span>}
							{client && <span className="ant-tag">{client}</span>}
							{project && <span className="ant-tag">{project} </span>}
							{businessUnitFilterValue && (
								<span className="ant-tag" style={{ width: '100px' }}>
									{businessUnitFilterValue}
								</span>
							)}
							{UserAllVal && <span className="ant-tag">{UserAllVal} </span>}
							{clientAll && <span className="ant-tag">{clientAll} </span>}
							{validArray.length > 0 && (
								<Tooltip title="Clear filter">
									<CloseOutlined
										className="filter-close-icon"
										onClick={() => {
											window.location = `${window.origin}/employees`;
										}}
									/>
								</Tooltip>
							)}
						</span>
					</Box>
				</Box>
				<Flex>
					{/* <ButtonWrapper onClick={exportfunc} icons={<DownloadOutlined />}>
						EXPORT
					</ButtonWrapper> */}
					<ButtonWrapper
						onClick={() =>
							setpaginateConfig({
								...initialPaginateConfig,
								search_type: 'employee',
							})
						}
						icons={<RedoOutlined />}
					>
						SYNC
					</ButtonWrapper>
					<ACL action="create" module="employee">
						<ButtonWrapper
							onClick={() => navigate('/employees/add')}
							icons={<PlusOutlined />}
						>
							CREATE
						</ButtonWrapper>
					</ACL>
				</Flex>
			</Flex>
			<Box mt={2}>
				<RCTable
					rowKey="id"
					tableDatas={[rowData, setRowData]}
					togglePaginate={[paginateConfig, setpaginateConfig]}
					dispatchAction={dispatchAction}
					columns={UserManagementTableConfig}
				/>
			</Box>
			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setOpen(false)}
				open={open}
			>
				<Box>
					<Box mb="2rem">
						<Text mb="5px">Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="status"
							value={paginateConfig?.is_active}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									is_active: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={StatusOptions}
						/>
					</Box>
					<Box marginBottom="2rem">
						<Text mb="5px">Type</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="user_type"
							value={paginateConfig?.user_type}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									user_type: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={UserTypesOptions}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Business Unit</Text>
						<Select
							mode="multiple"
							showSearch
							style={{ width: '100%' }}
							name="business_unit_ids"
							value={businessUnitIds}
							onChange={(val) => {
								setpaginateConfig((prev) => ({
									...prev,
									business_unit_ids: val,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={businessUnitsList}
							maxTagCount="responsive"
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">City</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="city"
							value={paginateConfig?.city}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									city: val,
								}))
							}
							allowClear
							onClear={() => {
								setpaginateConfig((prev) => ({
									...prev,
									city: '',
									client_id: undefined,
									project_id: '',
									vendor_id: '',
									trip_type: '',
								}));
							}}
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={citiesList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Client</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="client_name"
							value={paginateConfig?.client_id}
							onChange={(val) => {
								if (useV2API) {
									// V2: Store both client_id (for Select) and client_name (for API)
									const selectedClient = clientsList.find(
										(c) => c.value === val || c.value === String(val)
									);
									setpaginateConfig((prev) => ({
										...prev,
										client_id: val, // For Select component
										client_name: selectedClient?.label, // For V2 API
									}));
								} else {
									// V1: Store client_id only
									setpaginateConfig((prev) => ({
										...prev,
										client_id: val,
									}));
								}
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={clientsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Project</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="project_location_code"
							value={paginateConfig?.project_location_code}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									project_id: val,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={projectsList}
						/>
					</Box>
					<Box>
						{/* <ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={() =>
								setpaginateConfig((prev) => ({
									...prev,
									user_type: 'admin',
								}))
							}
						>
							RESET FILTERS
						</ButtonWrapper> */}
						<ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={() => {
								// navigate('/client-list');
								window.location = `${window.origin}/employees`;
							}}
						>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>
		</>
	);
}
